import { forwardRef, useRef } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { borderRadii } from 'app/spacing';
import { fontSizes, fontWeights } from 'app/typography';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';

export const ButtonGroupButtonBase = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  height: 36px;
  border: 1px solid var(--color-border-medium);
  border-radius: 2px;
  color: var(--color-font-placeholder);
  & > span {
    font-weight: ${fontWeights.bold};
    font-size: ${fontSizes.small};
    color: var(--color-font-placeholder);
  }
  background-color: var(--color-background-white);
  &:disabled {
    background-color: var(--color-background-light);
  }
  &:not(:disabled):hover {
    border-color: var(--color-button-secondary-hover);
  }
  &:focus {
    outline: none;
    border-color: var(--color-button-secondary-hover);
  }
  ${({ selected }) =>
    selected &&
    css`
      color: var(--color-button-secondary-hover);
      & > span {
        color: var(--color-button-secondary-hover);
      }
      border-color: var(--color-button-secondary-hover);
      &:disabled {
        color: var(--color-font-primary);
        border-color: var(--color-border-darker);
      }
    `}
  ${({ error }) =>
    error &&
    css`
      border-color: var(--color-button-warning-hover);
    `}
`;

const ButtonGroupButton = forwardRef(
  ({ option = {}, fieldId, disabled, ...props }, ref) => {
    const { assignFieldHandle } = useKeyBoardContext();
    const buttonRef = useRef();

    assignFieldHandle(fieldId, {
      customFocus: () => {
        buttonRef.current?.focus();
        return buttonRef.current;
      },
      disabled,
    });

    return (
      <ButtonGroupButtonBase ref={ref} disabled={disabled} {...props}>
        <span>{option.label}</span>
      </ButtonGroupButtonBase>
    );
  }
);

export default ButtonGroupButton;

export const ButtonGroupImageButtonBase = styled(ButtonGroupButtonBase)`
  display: inline-flex;
  flex-direction: column;
  ${({ customHeight }) =>
    customHeight
      ? css`
          height: ${customHeight}px;
        `
      : css`
          height: auto;
        `};
  border-radius: ${borderRadii.small};
  padding: 10px;
  padding-bottom: 7px;

  ${({ imageFullSizeBtn = false }) =>
    imageFullSizeBtn &&
    css`
      padding: 0;
    `}

  & > svg {
    max-width: 100%;
    ${({ hasText }) =>
      hasText
        ? css`
            margin-bottom: 8px;
          `
        : css`
            margin-bottom: 0;
          `}
  }
  & > span {
    font-size: ${fontSizes.text};
    font-weight: ${fontWeights.regular};
  }
`;

export function ButtonGroupImageButton({
  option,
  fieldId,
  disabled,
  ...props
}) {
  const { assignFieldHandle } = useKeyBoardContext();
  const buttonRef = useRef();

  assignFieldHandle(fieldId, {
    customFocus: () => {
      buttonRef.current?.focus();
      return buttonRef.current;
    },
    disabled,
  });

  return (
    <ButtonGroupImageButtonBase
      ref={buttonRef}
      hasText={Boolean(option?.label)}
      disabled={disabled}
      {...props}
    >
      {option?.image}
      <TextEllipsisWithTooltip as="span">
        {option?.label}
      </TextEllipsisWithTooltip>
    </ButtonGroupImageButtonBase>
  );
}

export const ButtonGroupImageBackgroundButtonBase = styled(
  ButtonGroupImageButtonBase
)`
  position: relative;
`;

const ButtonGroupImageBackgroundText = styled.div`
  position: absolute;
  top: 56px;
  font-size: ${fontSizes.text};
  font-weight: ${fontWeights.regular};
  ${({ selected }) =>
    selected &&
    css`
      color: var(--color-button-secondary-hover);
      &:disabled {
        color: var(--color-font-primary);
        border-color: var(--color-font-primary);
      }
    `}
`;
export function ButtonGroupImageBackgroundButton({ option, ...props }) {
  return (
    <ButtonGroupImageBackgroundButtonBase {...props}>
      {option.image}
      <ButtonGroupImageBackgroundText {...props}>
        {option.label}
      </ButtonGroupImageBackgroundText>
      {/* <span>{option.label}</span> */}
    </ButtonGroupImageBackgroundButtonBase>
  );
}
